export type MasterMatchCandidate = {
  id: string;
  name: string;
  code: string | null;
  /** Solo categorías: el producto puede guardar slug en `category_id`. */
  slug?: string | null;
};

function matchByIdOrSlug(candidates: MasterMatchCandidate[], ref: string): MasterMatchCandidate[] {
  const key = ref.trim().toLowerCase();
  return candidates.filter(
    (c) =>
      c.id.trim().toLowerCase() === key ||
      (c.slug != null && c.slug.trim().toLowerCase() === key)
  );
}

/** Prioridad: id explícito → código → nombre. */
export function resolveMasterReference(
  candidates: MasterMatchCandidate[],
  opts: { id?: string; name?: string; code?: string }
): { id: string | null; error?: string } {
  const idRef = opts.id?.trim();
  if (idRef) {
    const byId = matchByIdOrSlug(candidates, idRef);
    if (byId.length === 1) return { id: byId[0].id };
    if (byId.length > 1) return { id: null, error: `Varios registros con el mismo id "${idRef}".` };
    return { id: null, error: `No encontramos ningún registro con id "${idRef}".` };
  }

  const code = opts.code?.trim();
  if (code) {
    const byCode = candidates.filter(
      (c) => c.code != null && c.code.trim().toLowerCase() === code.toLowerCase()
    );
    if (byCode.length === 1) return { id: byCode[0].id };
    if (byCode.length > 1) return { id: null, error: `Varios registros con código "${code}".` };
  }

  const name = opts.name?.trim();
  if (!name) return { id: null };

  const byName = candidates.filter((c) => c.name.trim().toLowerCase() === name.toLowerCase());
  if (byName.length === 1) return { id: byName[0].id };
  if (byName.length > 1) return { id: null, error: `Varios registros con nombre "${name}".` };
  return { id: null };
}

/**
 * Una sola celda puede traer id interno (recomendado al reimportar export)
 * o nombre/código legible.
 */
export function resolveMasterFromCell(
  candidates: MasterMatchCandidate[],
  cellValue: string
): { id: string | null; error?: string } {
  const value = cellValue.trim();
  if (!value) return { id: null };

  const byId = matchByIdOrSlug(candidates, value);
  if (byId.length === 1) return { id: byId[0].id };
  if (byId.length > 1) {
    return { id: null, error: `Varios registros coinciden con "${value}". Usá la columna *_id del export.` };
  }

  return resolveMasterReference(candidates, { name: value, code: value });
}

/**
 * Reimportación desde export: si el id de la columna *_id falla (Excel lo alteró),
 * intenta la columna de nombre legible antes de dar error.
 */
export function resolveMasterFromRecordFields(
  candidates: MasterMatchCandidate[],
  opts: { id?: string; label?: string }
): { id: string | null; error?: string } {
  const idRef = opts.id?.trim();
  const label = opts.label?.trim();

  if (idRef) {
    const byId = resolveMasterReference(candidates, { id: idRef });
    if (byId.id) return byId;
    if (!label) return byId;
  }

  if (label) {
    return resolveMasterFromCell(candidates, label);
  }

  return { id: null };
}

/** Id canónico de categoría/marca/proveedor (slug, uuid o nombre resuelto). */
export function canonicalMasterCandidateId(
  ref: string | null | undefined,
  candidates: MasterMatchCandidate[]
): string | null {
  const trimmed = ref?.trim();
  if (!trimmed) return null;
  const matches = matchByIdOrSlug(candidates, trimmed);
  if (matches.length === 1) return matches[0].id;
  const byName = resolveMasterReference(candidates, { name: trimmed, code: trimmed });
  return byName.id;
}

export function masterCandidateIdsEqual(
  a: string | null | undefined,
  b: string | null | undefined,
  candidates: MasterMatchCandidate[]
): boolean {
  const emptyA = !a?.trim();
  const emptyB = !b?.trim();
  if (emptyA && emptyB) return true;

  const canonA = canonicalMasterCandidateId(a, candidates);
  const canonB = canonicalMasterCandidateId(b, candidates);
  if (canonA && canonB) return canonA.toLowerCase() === canonB.toLowerCase();

  const rawA = a?.trim().toLowerCase() ?? '';
  const rawB = b?.trim().toLowerCase() ?? '';
  return rawA === rawB;
}
